import * as enabledApi from '@shared/api/enabledPlayApi';
import { StopAllOutputsCommand } from '@shared/constants/CommandValues';
import { EnabledPlayDesktopVirtualDeviceTypeId } from '@shared/constants/DeviceIds';
import { createUUID } from '@shared/constants/Keys';
import { isLicenseValidForProfile } from '@shared/constants/profile/ProfileUtils';
import { DEVICES, VIRTUAL_DEVICE, VIRTUAL_DEVICE_ID, VIRTUAL_DEVICE_TOKEN } from '@shared/constants/StorageValues';
import { isCommandInProfile } from '@shared/hooks/helpers/DeviceUtils';
import IUserAccess from '@shared/models/components/IUserAccess';
import MacroModel from '@shared/models/deviceProfiles/MacroModel';
import {
    applyMacroJitter,
    enforceRequiredPauses,
    validateIncompatibleActions,
} from '@shared/services/ThrottleRuleEnforcer';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import DeviceTypeClientListingModel from '@shared/models/devices/DeviceTypeClientListingModel';
import UpdateUserDeviceRequest from '@shared/models/devices/UpdateUserDeviceRequest';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import UserDeviceTokenModel from '@shared/models/devices/UserDeviceTokenModel';
import InvalidResult from '@shared/models/result/InvalidResult';
import IResult from '@shared/models/result/IResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { create } from 'zustand';
import { publish, subscribe } from '../helpers/EventCallback';

// Module-level refs not tied to React lifecycle
export const deviceInitiatorRef = createUUID();
const currentAppNameRef: { current: string | null } = { current: null };
const userLicenseAccessRef: { current: IUserAccess | undefined } = { current: undefined };
const deviceThrottleStateRef: {
    current: { [userDeviceId: string]: { perSecond: number[]; perMinute: number[] } };
} = { current: {} };
const throttleWarningHandlersRef: {
    current: ((throttleType: 'macroLength' | 'commandCount' | 'incompatibleActions') => void)[];
} = {
    current: [],
};

const computeConnectedDevices = (devices: UserDeviceModel[], virtualDeviceId: string | null) =>
    devices.filter((d) => d.id === virtualDeviceId || d.isConnected);

interface DeviceState {
    // State
    connectedDevices: UserDeviceModel[];
    deviceCommandTokens: { userDeviceId: string; token: UserDeviceTokenModel }[];
    devices: UserDeviceModel[];
    deviceTypeClientList: DeviceTypeClientListingModel[];
    deviceTypesInitializing: boolean;
    errors: string[];
    isInitialized: boolean;
    isLoading: boolean;
    isSwitchingTempDisabled: boolean;
    outputDeviceId: string | null;
    throttleWarningHandlers: ((throttleType: 'macroLength' | 'commandCount' | 'incompatibleActions') => void)[];
    tryCommandHandlers: ((command: string, profile: UserDeviceProfileModel) => boolean)[];
    tryGlobalControlHandlers: ((command: string, userDeviceId: string) => boolean)[];
    virtualDeviceId: string | null;
    virtualDeviceToken: UserDeviceTokenModel | null;

    // Refs
    throttleWarningHandlersRef: {
        current: ((throttleType: 'macroLength' | 'commandCount' | 'incompatibleActions') => void)[];
    };

    addThrottleWarningHandler: (
        handler: (throttleType: 'macroLength' | 'commandCount' | 'incompatibleActions') => void
    ) => void;
    removeThrottleWarningHandlers: () => void;
    addTryCommandHandler: (handler: (command: string, profile: UserDeviceProfileModel) => boolean) => void;
    removeTryCommandHandler: () => void;
    addTryGlobalControlHandler: (handler: (command: string, userDeviceId: string) => boolean) => void;
    removeTryGlobalControlHandler: () => void;
    checkForUpdates: (userDeviceId: string) => Promise<IResult<UserDeviceModel>>;
    clearDevices: () => void;
    deleteDevice: (userDeviceId: string) => Promise<IResult<UserDeviceModel>>;
    disableDevice: (userDeviceId: string) => Promise<IResult<UserDeviceModel>>;
    fallbackToDeviceSwitching: () => void;
    getDeviceName: (deviceId: string) => string;
    getDevices: () => Promise<IResult<UserDeviceModel[]>>;
    getLocalDevice: (userDeviceId: string | null) => UserDeviceModel | null;
    initializeDeviceTypesClientList: (locale: string) => Promise<void>;
    initializeFromStorage: () => void;
    initializeFromRemote: (
        computerName: string | undefined,
        hardwareId: string | undefined,
        platform?: string
    ) => Promise<IResult<UserDeviceModel[]>>;
    initializeVirtualController: (
        computerName?: string | undefined,
        hardwareId?: string | undefined,
        platform?: string
    ) => Promise<IResult<[UserDeviceModel | null, UserDeviceTokenModel | null]>>;
    isAutoProfileSwitchingDisabled: () => boolean;
    isDesktopApp: (deviceTypeId: string) => boolean;
    isDeviceVirtualController: (deviceId: string) => boolean;
    overrideAppSwitching: () => void;
    registerNewVirtualDevice: (
        computerName?: string | undefined,
        hardwareId?: string | undefined,
        platform?: string
    ) => Promise<IResult<[UserDeviceModel | null, UserDeviceTokenModel | null]>>;
    removeLocalDevice: (userDeviceId: string) => void;
    resetDeviceProfile: (deviceId: string | null, profile: UserDeviceProfileModel) => Promise<IResult<UserDeviceModel>>;
    sendDeviceCommand: (userDeviceId: string, command: string, macro?: MacroModel) => Promise<boolean>;
    setDevices: (devices: UserDeviceModel[]) => void;
    setDeviceProfile: (
        deviceId: string | undefined,
        profile: UserDeviceProfileModel
    ) => Promise<IResult<UserDeviceModel>>;
    startListening: (userDeviceId: string) => Promise<IResult<UserDeviceModel>>;
    stopAllOutputs: (userDeviceId: string, profile: UserDeviceProfileModel) => Promise<void>;
    stopListening: (userDeviceId: string) => Promise<IResult<UserDeviceModel>>;
    tryDeviceCommand: (
        userDeviceId: string,
        command: string,
        profile: UserDeviceProfileModel,
        additionalInputContent?: string
    ) => Promise<boolean>;
    tryDeviceTypeCommand: (userDeviceId: string, phrase: string) => Promise<boolean>;
    updateDevice: (userDeviceId: string, update: UpdateUserDeviceRequest) => Promise<IResult<UserDeviceModel>>;
    updateLocalDevice: (device: UserDeviceModel) => void;
    updateVirtualDevice: (device: UserDeviceModel) => void;
    updateCurrentAppName: (appName: string) => boolean;
    updateOutputDeviceId: (deviceId: string | null) => void;
}

export const useDeviceStore = create<DeviceState>()((set, get) => {
    const removeThrottleWarningHandlers = () => set({ throttleWarningHandlers: [] });
    const removeTryCommandHandler = () => set({ tryCommandHandlers: [] });
    const removeTryGlobalControlHandler = () => set({ tryGlobalControlHandlers: [] });
    const getDeviceToken = async (userDeviceId: string) => {
        const { virtualDeviceId, virtualDeviceToken, deviceCommandTokens } = get();
        if (userDeviceId === virtualDeviceId) {
            return virtualDeviceToken;
        }
        const existingToken = deviceCommandTokens.find((x) => x.userDeviceId === userDeviceId && !x.token.isDisabled);
        if (existingToken) {
            return existingToken.token;
        }
        const result = await enabledApi.generateCommandToken(userDeviceId);
        if (result?.resultType === 'Ok' && result.data) {
            set((state) => ({
                deviceCommandTokens: [...state.deviceCommandTokens, { userDeviceId, token: result.data! }],
            }));
            return result.data;
        }
    };

    const snapshotState = (
        devices: UserDeviceModel[],
        virtualDeviceId: string | null,
        virtualDeviceToken: UserDeviceTokenModel | null
    ) => {
        if (devices?.length > 0) localStorage.setItem(DEVICES, JSON.stringify(devices));
        if (virtualDeviceId) localStorage.setItem(VIRTUAL_DEVICE_ID, virtualDeviceId);
        if (virtualDeviceToken) localStorage.setItem(VIRTUAL_DEVICE_TOKEN, JSON.stringify(virtualDeviceToken));
    };

    // Falls back to localStorage because this can be called before the store is
    // hydrated (e.g. during initializeVirtualController at app startup).
    const getLocalVirtualDevice = (): UserDeviceModel | null => {
        const { virtualDeviceId, devices } = get();
        if (virtualDeviceId) {
            const device = devices?.find((d) => d.id === virtualDeviceId);
            if (device) return device;
        }
        const storedVirtualDevice = localStorage.getItem(VIRTUAL_DEVICE);
        const storedVirtualDeviceId = localStorage.getItem(VIRTUAL_DEVICE_ID);
        const device = storedVirtualDevice ? JSON.parse(storedVirtualDevice) : null;
        if (device) return device;
        if (!storedVirtualDeviceId) return null;
        return devices?.find((d) => d.id === storedVirtualDeviceId) ?? null;
    };

    // Falls back to localStorage because this can be called before the store is
    // hydrated (e.g. during initializeVirtualController at app startup).
    const getLocalVirtualDeviceToken = (): UserDeviceTokenModel | null => {
        const { virtualDeviceToken } = get();
        if (virtualDeviceToken) {
            return virtualDeviceToken;
        }
        const virtualDeviceTokenLocal = localStorage.getItem(VIRTUAL_DEVICE_TOKEN);
        if (virtualDeviceTokenLocal) {
            return JSON.parse(virtualDeviceTokenLocal);
        }
        return null;
    };

    const isDeviceEqual = (a: UserDeviceModel, b: UserDeviceModel): boolean => {
        return JSON.stringify(a) === JSON.stringify(b);
    };

    const updateLocalDevice = (device: UserDeviceModel) => {
        const { devices: currentDevices, virtualDeviceId, virtualDeviceToken } = get();
        const index = currentDevices.findIndex((x) => x.id === device.id);

        // Skip update if device hasn't actually changed
        if (index > -1 && isDeviceEqual(currentDevices[index], device)) return;

        const updatedDevices =
            index > -1 ? currentDevices.map((d, i) => (i === index ? device : d)) : [...currentDevices, device];
        set({
            devices: updatedDevices,
            connectedDevices: computeConnectedDevices(updatedDevices, virtualDeviceId),
        });
        snapshotState(updatedDevices, virtualDeviceId, virtualDeviceToken);
    };

    const updateDevicePlatform = async (device: UserDeviceModel, platform: string = 'generic') => {
        if (device?.platform === platform) return;
        await get().updateDevice(device.id, { ...device, platform });
    };

    const setDevices = (devices: UserDeviceModel[]) => {
        const currentDevices = get().devices;
        // Skip update if devices array hasn't actually changed
        if (currentDevices.length === devices.length && devices.every((d, i) => isDeviceEqual(currentDevices[i], d))) {
            return;
        }
        set((state) => ({
            devices,
            connectedDevices: computeConnectedDevices(devices, state.virtualDeviceId),
        }));
    };

    const addTryCommandHandler = (handler: (command: string, profile: UserDeviceProfileModel) => boolean) =>
        set((state) => ({ tryCommandHandlers: [...state.tryCommandHandlers, handler] }));

    const addTryGlobalControlHandler = (handler: (command: string, userDeviceId: string) => boolean) =>
        set((state) => ({ tryGlobalControlHandlers: [...state.tryGlobalControlHandlers, handler] }));

    const addThrottleWarningHandler = (
        handler: (throttleType: 'macroLength' | 'commandCount' | 'incompatibleActions') => void
    ) => {
        throttleWarningHandlersRef.current = [...throttleWarningHandlersRef.current, handler];
        set((state) => ({ throttleWarningHandlers: [...state.throttleWarningHandlers, handler] }));
    };

    const initializeFromStorage = () => {
        try {
            set({ isInitialized: false });
            const localDevices = localStorage.getItem(DEVICES);
            let storedDevices: UserDeviceModel[] = [];
            if (localDevices) {
                storedDevices = JSON.parse(localDevices);
            }
            // Resolve virtual device directly from storedDevices + localStorage
            // so we don't depend on store state which is still empty at this point.
            const storedVirtualDeviceRaw = localStorage.getItem(VIRTUAL_DEVICE);
            const storedVirtualDeviceId = localStorage.getItem(VIRTUAL_DEVICE_ID);
            const virtualDevice: UserDeviceModel | null =
                (storedVirtualDeviceRaw ? JSON.parse(storedVirtualDeviceRaw) : null) ??
                (storedVirtualDeviceId ? (storedDevices.find((d) => d.id === storedVirtualDeviceId) ?? null) : null);
            const virtualDeviceToken = getLocalVirtualDeviceToken();
            set((state) => ({
                devices: storedDevices,
                connectedDevices: computeConnectedDevices(storedDevices, virtualDevice?.id ?? null),
                virtualDeviceId: virtualDevice?.id ?? null,
                virtualDeviceToken,
                isInitialized: true,
            }));
        } catch (e) {
            console.log('Error loading devices from storage', e);
            set({ isInitialized: true });
        }
    };

    const initializeFromRemote = async (
        computerName: string | undefined,
        hardwareId: string | undefined,
        platform: string = 'generic'
    ): Promise<IResult<UserDeviceModel[]>> => {
        try {
            set({ isInitialized: false });
            await get().initializeVirtualController(computerName, hardwareId, platform);
            const deviceLoadResult = await get().getDevices();
            return deviceLoadResult;
        } catch (error) {
            console.error('[ERROR initializeFromRemote]', error);
            return new UnexpectedResult();
        } finally {
            set({ isInitialized: true, isLoading: false });
        }
    };

    const registerNewVirtualDevice = async (
        computerName: string | undefined,
        hardwareId: string | undefined,
        platform: string = 'generic'
    ): Promise<IResult<[UserDeviceModel | null, UserDeviceTokenModel | null]>> => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.registerNewDevice(
                EnabledPlayDesktopVirtualDeviceTypeId,
                computerName,
                hardwareId,
                platform
            );
            if (result?.resultType === 'Ok' && result.data) {
                const tokenResult = await enabledApi.generateUserDeviceToken(result.data.id, {
                    includeUserEvents: true,
                });
                const token = tokenResult.resultType === 'Ok' && tokenResult.data ? tokenResult.data : null;
                set((state) => ({
                    virtualDeviceId: result.data!.id,
                    virtualDeviceToken: token,
                }));
                snapshotState(get().devices, result.data.id, token);
                return new SuccessResult([result.data, token]);
            } else if (result.errors != null && result.errors.length > 0) {
                set({ errors: [...result.errors] });
            }
            return { ...result, data: [null, null] };
        } catch (error) {
            set({ errors: [error] });
            console.debug('[ERROR registerNewVirtualDevice]', error);
            return new UnexpectedResult();
        } finally {
            set({ isLoading: false });
        }
    };

    const initializeVirtualController = async (
        computerName: string | undefined,
        hardwareId?: string,
        platform: string = 'generic'
    ): Promise<IResult<[UserDeviceModel | null, UserDeviceTokenModel | null]>> => {
        try {
            const existingVirtualDevice = getLocalVirtualDevice();
            const existingToken = getLocalVirtualDeviceToken();
            if (existingVirtualDevice && existingToken) {
                set({
                    virtualDeviceId: existingVirtualDevice.id,
                    virtualDeviceToken: existingToken,
                });
                await updateDevicePlatform(existingVirtualDevice, platform);
                return new SuccessResult([existingVirtualDevice, existingToken]);
            }
            return await get().registerNewVirtualDevice(computerName, hardwareId, platform);
        } catch (error) {
            set({ errors: [error] });
            console.debug('[ERROR initializeVirtualController]', error);
            return new UnexpectedResult();
        }
    };

    const getDevices = async () => {
        try {
            set({ isLoading: true });
            const result = await enabledApi.getUserDevices();
            if (result?.resultType === 'Ok' && result.data) {
                const loadedDevices = result.data.filter((d) => d.isVerified);
                const { virtualDeviceId, virtualDeviceToken } = get();
                set({
                    devices: loadedDevices,
                    connectedDevices: computeConnectedDevices(loadedDevices, virtualDeviceId),
                });
                snapshotState(loadedDevices, virtualDeviceId, virtualDeviceToken);
                return new SuccessResult<UserDeviceModel[]>(loadedDevices);
            }
            return result;
        } catch (e) {
            set({ errors: ['Error getting devices'] });
            return new UnexpectedResult<UserDeviceModel[]>();
        } finally {
            set({ isLoading: false });
        }
    };

    const getLocalDevice = (userDeviceId: string | null) => {
        if (!userDeviceId) return null;
        return get().devices?.find((d) => d.id === userDeviceId) ?? null;
    };

    const getDeviceName = (deviceId: string) => {
        const device = get().getLocalDevice(deviceId);
        if (device) {
            return device.nameOverride ?? device.device?.name ?? device.device.deviceType?.name ?? '-';
        }
        return '-';
    };

    const updateVirtualDevice = (device: UserDeviceModel) => updateLocalDevice(device);

    const updateDevice = async (userDeviceId: string, update: UpdateUserDeviceRequest) => {
        const result = await enabledApi.updateUserDevice(userDeviceId, update);
        if (result?.resultType === 'Ok' && result.data) {
            updateLocalDevice(result.data);
        }
        return result;
    };

    const deleteDevice = async (userDeviceId: string) => {
        try {
            const result = await enabledApi.unregisterUserDevice(userDeviceId);
            if (result?.resultType === 'Ok') {
                const { devices, virtualDeviceId, virtualDeviceToken, outputDeviceId } = get();
                const updatedDevices = devices.filter((d) => d.id !== userDeviceId);
                const isVirtualDevice = virtualDeviceId === userDeviceId;
                if (isVirtualDevice) {
                    set({
                        virtualDeviceId: null,
                        virtualDeviceToken: null,
                    });
                    localStorage.removeItem(VIRTUAL_DEVICE_ID);
                    localStorage.removeItem(VIRTUAL_DEVICE_TOKEN);
                    localStorage.removeItem(VIRTUAL_DEVICE);
                }
                if (outputDeviceId === userDeviceId) {
                    set({ outputDeviceId: null });
                }
                const newVirtualId = isVirtualDevice ? null : virtualDeviceId;
                set({
                    devices: updatedDevices,
                    connectedDevices: computeConnectedDevices(updatedDevices, newVirtualId),
                });
                snapshotState(updatedDevices, newVirtualId, isVirtualDevice ? null : virtualDeviceToken);
            }
            return result;
        } catch (e) {
            return new UnexpectedResult<UserDeviceModel>();
        }
    };

    const disableDevice = async (userDeviceId: string) => {
        try {
            const result = await enabledApi.disableDevice(userDeviceId);
            if (result?.resultType === 'Ok') {
                const { devices, virtualDeviceId, virtualDeviceToken, outputDeviceId } = get();
                const deviceIndex = devices.findIndex((x) => x.id === userDeviceId);
                if (deviceIndex !== -1) {
                    const updatedDevices = devices.filter((_, i) => i !== deviceIndex);
                    const isVirtualDevice = virtualDeviceId === userDeviceId;
                    if (isVirtualDevice) {
                        set({
                            virtualDeviceId: null,
                            virtualDeviceToken: null,
                        });
                        localStorage.removeItem(VIRTUAL_DEVICE_ID);
                        localStorage.removeItem(VIRTUAL_DEVICE_TOKEN);
                        localStorage.removeItem(VIRTUAL_DEVICE);
                    }
                    if (outputDeviceId === userDeviceId) {
                        set({ outputDeviceId: null });
                    }
                    const newVirtualId = isVirtualDevice ? null : virtualDeviceId;
                    set({
                        devices: updatedDevices,
                        connectedDevices: computeConnectedDevices(updatedDevices, newVirtualId),
                    });
                    snapshotState(updatedDevices, newVirtualId, isVirtualDevice ? null : virtualDeviceToken);
                }
            }
            return result;
        } catch (e) {
            return new UnexpectedResult<UserDeviceModel>();
        }
    };

    const removeLocalDevice = (userDeviceId: string) => {
        const { devices, virtualDeviceId, virtualDeviceToken } = get();
        const updatedDevices = devices.filter((d) => d.id !== userDeviceId);
        const isVirtualDevice = virtualDeviceId === userDeviceId;
        const newVirtualId = isVirtualDevice ? null : virtualDeviceId;
        set({
            devices: updatedDevices,
            connectedDevices: computeConnectedDevices(updatedDevices, newVirtualId),
        });
        snapshotState(updatedDevices, newVirtualId, virtualDeviceToken);
    };

    const checkForUpdates = async (userDeviceId: string) => {
        const token = await getDeviceToken(userDeviceId);
        if (!token) return new UnexpectedResult<UserDeviceModel>();
        return await enabledApi.updateDeviceFirmware(userDeviceId, token.token);
    };

    const setDeviceProfile = async (
        deviceId: string | undefined,
        profile: UserDeviceProfileModel
    ): Promise<IResult<UserDeviceModel>> => {
        try {
            if (!deviceId) return new InvalidResult('Device ID is required to set profile');
            const device = get().devices?.find((d) => d.id === deviceId);
            if (!device) return new InvalidResult('Device not found');
            if (!isLicenseValidForProfile(profile, userLicenseAccessRef.current)) {
                return new InvalidResult('Invalid permissions. Cannot set device profile');
            }

            let result: IResult<UserDeviceModel> = new UnexpectedResult<UserDeviceModel>();
            let updatedDevice = device;
            const profileUpdate = profile.isPublic
                ? { customProfileId: null, defaultProfileId: profile.id }
                : { customProfileId: profile.id };
            result = await enabledApi.updateDeviceProfile(deviceId, profileUpdate);
            if (result.resultType === 'Ok' && result.data) {
                updatedDevice = result.data;
            }
            if (result?.resultType === 'NoResponse' || result?.resultType === 'Offline') {
                updatedDevice = { ...device };
                if (profile.isPublic) {
                    updatedDevice.currentProfile = null;
                    updatedDevice.defaultProfileId = profile.id;
                } else {
                    updatedDevice.currentProfile = profile;
                }
                result = new SuccessResult<UserDeviceModel>(updatedDevice);
            }
            updateLocalDevice(updatedDevice);
            return result;
        } catch (e) {
            return new UnexpectedResult<UserDeviceModel>();
        }
    };

    const resetDeviceProfile = async (
        deviceId: string | null,
        profile: UserDeviceProfileModel
    ): Promise<IResult<UserDeviceModel>> => {
        if (!deviceId) return new InvalidResult<UserDeviceModel>('Device ID is not set');
        const device = get().devices?.find((d) => d.id === deviceId);
        if (!device) return new InvalidResult<UserDeviceModel>('Device not found');

        let result: IResult<UserDeviceModel>;
        if (profile?.isPublic) {
            result = await enabledApi.updateDeviceProfile(deviceId, { defaultProfileId: null });
        } else {
            result = await enabledApi.updateDeviceProfile(deviceId, { customProfileId: null });
        }

        let updatedDevice = device;
        if (result.resultType === 'Ok' && result.data) {
            updatedDevice = result.data;
        } else if (result.resultType !== 'Ok') {
            updatedDevice = { ...device };
            if (profile?.isPublic) {
                updatedDevice.defaultProfileId = null;
            } else {
                updatedDevice.currentProfile = null;
            }
            result = new SuccessResult<UserDeviceModel>(updatedDevice);
        }
        updateLocalDevice(updatedDevice);
        return result;
    };

    const startListening = async (userDeviceId: string) => {
        const token = await getDeviceToken(userDeviceId);
        if (!token) return new UnexpectedResult<UserDeviceModel>();
        return await enabledApi.startDeviceListening(userDeviceId, token.token);
    };

    const stopListening = async (userDeviceId: string) => {
        const token = await getDeviceToken(userDeviceId);
        if (!token) return new UnexpectedResult<UserDeviceModel>();
        const device = get().devices?.find((d) => d.id === userDeviceId);
        if (!device || !device.isListening) return new UnexpectedResult<UserDeviceModel>();
        return await enabledApi.stopDeviceListening(userDeviceId, token.token);
    };

    const stopAllOutputs = async (userDeviceId: string, profile: UserDeviceProfileModel) => {
        await get().tryDeviceCommand(userDeviceId, StopAllOutputsCommand, profile);
    };

    const tryDeviceCommand = async (
        userDeviceId: string,
        command: string,
        profile: UserDeviceProfileModel,
        additionalInputContent?: any
    ) => {
        const { tryCommandHandlers, tryGlobalControlHandlers, virtualDeviceId, devices } = get();
        if (userDeviceId === virtualDeviceId) {
            if (profile) {
                for (const handler of tryCommandHandlers) {
                    if (handler(command, profile)) return true;
                }
            }
        } else {
            const device = devices?.find((d) => d.id === userDeviceId);
            if (device && profile && !isCommandInProfile(command, profile)) {
                for (const handler of tryGlobalControlHandlers) {
                    if (handler(command, userDeviceId)) return true;
                }
                return false;
            }
            let macro = device?.currentProfile?.configuration?.macros?.find((m) =>
                m.commands.some((c) => c.toLowerCase() === command.toLowerCase())
            );
            const keybinding = device?.currentProfile?.configuration?.commandKeyMappings.find((k) =>
                k.commands.some((c) => c.toLowerCase() === command.toLowerCase())
            );
            if (!macro && keybinding && keybinding.key) {
                macro = {
                    id: 'keybind' + keybinding.key,
                    name: 'keybind',
                    commands: [command],
                    events: [
                        {
                            keys: [keybinding.key],
                            eventType: 'KeyPress',
                            isKeyLatch: false,
                            holdTimeMilliseconds: 0,
                        },
                    ],
                };
            }
            return await get().sendDeviceCommand(userDeviceId, command, macro);
        }
        return false;
    };

    const tryDeviceTypeCommand = async (userDeviceId: string, phrase: string) => {
        const device = get().devices?.find((d) => d.id === userDeviceId);
        if (device) {
            const token = await getDeviceToken(userDeviceId);
            if (token) {
                await enabledApi.sendDeviceTypeCommand(userDeviceId, phrase, token.token);
                return true;
            }
        }
        return false;
    };

    const sendDeviceCommand = async (userDeviceId: string, command: string, macro?: MacroModel) => {
        const device = get().devices?.find((d) => d.id === userDeviceId);
        if (!device) return false;

        const throttleRules = device.device?.deviceType?.commandThrottleRules;

        let macroToSend = macro ? { ...macro } : undefined;
        if (macroToSend && throttleRules?.maximumMacroLength != null && throttleRules.maximumMacroLength > 0) {
            if (macroToSend.events && macroToSend.events.length > throttleRules.maximumMacroLength) {
                macroToSend.events = macroToSend.events.slice(0, throttleRules.maximumMacroLength);
                throttleWarningHandlersRef.current?.forEach((handler) => handler('macroLength'));
            }
        }
        if (macroToSend?.events && throttleRules) {
            if (throttleRules.incompatibleActionGroups?.length) {
                const violation = validateIncompatibleActions(
                    macroToSend.events,
                    throttleRules.incompatibleActionGroups
                );
                if (violation) {
                    throttleWarningHandlersRef.current?.forEach((handler) => handler('incompatibleActions'));
                    return false;
                }
            }
            if (throttleRules.requiredPauseBetweenRules?.length) {
                macroToSend.events = enforceRequiredPauses(macroToSend.events, throttleRules.requiredPauseBetweenRules);
            }
            macroToSend.events = applyMacroJitter(macroToSend.events, throttleRules);
        }
        if (macroToSend) macroToSend.issuedAt = new Date().toISOString();

        const now = Date.now();
        if (!deviceThrottleStateRef.current[userDeviceId]) {
            deviceThrottleStateRef.current[userDeviceId] = { perSecond: [], perMinute: [] };
        }
        const state = deviceThrottleStateRef.current[userDeviceId];
        state.perSecond = state.perSecond.filter((ts) => now - ts < 1000);
        state.perMinute = state.perMinute.filter((ts) => now - ts < 60000);

        const maxPerSecond =
            throttleRules?.maximumCommandsPerSecond && throttleRules.maximumCommandsPerSecond > 0
                ? throttleRules.maximumCommandsPerSecond
                : null;
        if (maxPerSecond && state.perSecond.length >= maxPerSecond) {
            throttleWarningHandlersRef.current?.forEach((handler) => handler('commandCount'));
            return false;
        }
        const maxPerMinute =
            throttleRules?.maximumCommandsPerMinute && throttleRules.maximumCommandsPerMinute > 0
                ? throttleRules.maximumCommandsPerMinute
                : null;
        if (maxPerMinute && state.perMinute.length >= maxPerMinute) {
            throttleWarningHandlersRef.current?.forEach((handler) => handler('commandCount'));
            return false;
        }

        state.perSecond.push(now);
        state.perMinute.push(now);

        const token = await getDeviceToken(userDeviceId);
        if (token) {
            await enabledApi.sendDeviceCommand(userDeviceId, command, token.token, macroToSend);
            return true;
        }
        return false;
    };

    const updateCurrentAppName = (appName: string) => {
        if (appName !== currentAppNameRef.current) {
            currentAppNameRef.current = appName;
            get().fallbackToDeviceSwitching();
            return true;
        }
        return false;
    };

    const updateOutputDeviceId = (deviceId: string | null) => {
        const { connectedDevices, outputDeviceId, virtualDeviceId } = get();
        if (deviceId !== outputDeviceId) {
            set({ outputDeviceId: deviceId });
            const device = get().getLocalDevice(deviceId);
            if (connectedDevices.length > 1 && device) {
                publish('analyticsTrackEvent', {
                    eventName: 'output_device_switched',
                    data: { deviceTypeId: device?.device?.deviceType?.id },
                });
            }
        }
        if (deviceId === virtualDeviceId) {
            get().fallbackToDeviceSwitching();
        }
    };

    const fallbackToDeviceSwitching = () => {
        set({ isSwitchingTempDisabled: false });
    };

    const overrideAppSwitching = () => {
        if (!get().isSwitchingTempDisabled) {
            set({ isSwitchingTempDisabled: true });
        }
    };

    const isAutoProfileSwitchingDisabled = () => {
        const { outputDeviceId, devices, isSwitchingTempDisabled } = get();
        if (!outputDeviceId) return true;
        const device = devices?.find((d) => d.id === outputDeviceId);
        return (
            !get().isDeviceVirtualController(outputDeviceId) ||
            !device?.isAutoProfileSwitchingEnabled ||
            isSwitchingTempDisabled
        );
    };

    const isDeviceVirtualController = (deviceId: string) => deviceId === get().virtualDeviceId;

    const isDesktopApp = (deviceTypeId: string) => deviceTypeId === EnabledPlayDesktopVirtualDeviceTypeId;

    const clearDevices = () => {
        set({
            devices: [],
            connectedDevices: [],
            virtualDeviceId: null,
            virtualDeviceToken: null,
            outputDeviceId: null,
            deviceTypeClientList: [],
        });
        snapshotState([], null, null);
    };

    const initializeDeviceTypesClientList = async (locale: string) => {
        set({ deviceTypesInitializing: true });
        try {
            const result = await enabledApi.getDeviceTypesPublicClientListingLocale(locale);
            if (result && result.data) {
                set({ deviceTypeClientList: result.data });
            }
        } catch (e) {
            set({ deviceTypeClientList: [] });
        } finally {
            set({ deviceTypesInitializing: false });
        }
    };

    return {
        // State
        connectedDevices: [],
        deviceCommandTokens: [],
        devices: [],
        deviceTypeClientList: [],
        deviceTypesInitializing: true,
        errors: [],
        isInitialized: false,
        isLoading: true,
        isSwitchingTempDisabled: false,
        outputDeviceId: null,
        throttleWarningHandlers: [],
        tryCommandHandlers: [],
        tryGlobalControlHandlers: [],
        virtualDeviceId: null,
        virtualDeviceToken: null,

        // Refs
        throttleWarningHandlersRef,

        // Actions
        addThrottleWarningHandler,
        addTryCommandHandler,
        addTryGlobalControlHandler,
        removeThrottleWarningHandlers,
        removeTryGlobalControlHandler,
        removeTryCommandHandler,
        checkForUpdates,
        clearDevices,
        deleteDevice,
        disableDevice,
        fallbackToDeviceSwitching,
        getDeviceName,
        getDevices,
        getLocalDevice,
        initializeDeviceTypesClientList,
        initializeFromRemote,
        initializeFromStorage,
        initializeVirtualController,
        isAutoProfileSwitchingDisabled,
        isDesktopApp,
        isDeviceVirtualController,
        overrideAppSwitching,
        registerNewVirtualDevice,
        removeLocalDevice,
        resetDeviceProfile,
        sendDeviceCommand,
        setDeviceProfile,
        setDevices,
        startListening,
        stopAllOutputs,
        stopListening,
        tryDeviceCommand,
        tryDeviceTypeCommand,
        updateCurrentAppName,
        updateDevice,
        updateLocalDevice,
        updateOutputDeviceId,
        updateVirtualDevice,
    };
});

// Module-level event subscriptions (replace useEffect([], []))
subscribe('userLicenseChangeLocal', async (data: IUserAccess) => {
    if (data) {
        userLicenseAccessRef.current = data;
    }
});

subscribe('device-added', (data) => {
    useDeviceStore.getState().updateLocalDevice(data.device);
});

subscribe('remove-device', (data) => {
    useDeviceStore.getState().removeLocalDevice(data.device.id);
});
